/**
 * Centralized frontend runtime configuration.
 *
 * Single source of truth for API / socket hosts. Values come from CRA
 * `REACT_APP_*` environment variables at build time, with defaults that
 * match the historical production endpoints (unchanged behavior when unset).
 *
 * Local / staging / production: set env vars (see `.env.example`).
 * Do not scatter host strings across components — import from here or via
 * `apiConstants` / `Environment`.
 */

const DEFAULT_API_URL = "https://api.pentecostplus.com/userApi/";
const DEFAULT_SOCKET_URL = "https://api.pentecostplus.com:3003/";

function ensureTrailingSlash(url) {
  if (!url) {
    return url;
  }
  return url.endsWith("/") ? url : url + "/";
}

function readEnv(name) {
  const value = process.env[name];
  if (value === undefined || value === null) {
    return undefined;
  }
  const trimmed = String(value).trim();
  return trimmed === "" ? undefined : trimmed;
}

/**
 * Derive https://host/api/v1/auth/config from https://host/userApi/
 * @param {string} userApiUrl
 * @returns {string}
 */
function deriveAuthConfigUrl(userApiUrl) {
  const trimmed = String(userApiUrl || "").replace(/\/+$/, "");
  if (/\/userApi$/i.test(trimmed)) {
    return trimmed.replace(/\/userApi$/i, "/api/v1/auth/config");
  }
  const origin = trimmed.replace(/\/userApi\/.*$/i, "");
  return origin.replace(/\/+$/, "") + "/api/v1/auth/config";
}

/**
 * Derive Blade admin base (…/admin) from userApi URL.
 * @param {string} userApiUrl
 * @returns {string}
 */
function deriveAdminBaseUrl(userApiUrl) {
  const trimmed = String(userApiUrl || "").replace(/\/+$/, "");
  if (/\/userApi$/i.test(trimmed)) {
    return trimmed.replace(/\/userApi$/i, "/admin");
  }
  const origin = trimmed.replace(/\/userApi\/.*$/i, "");
  return origin.replace(/\/+$/, "") + "/admin";
}

function buildRuntimeConfig() {
  const apiUrl = ensureTrailingSlash(
    readEnv("REACT_APP_API_URL") || DEFAULT_API_URL
  );

  const settingsUrl =
    readEnv("REACT_APP_SETTINGS_URL") || apiUrl + "get_settings_json";

  const homeSettingsUrl =
    readEnv("REACT_APP_HOME_SETTINGS_URL") ||
    apiUrl + "get_home_settings_json";

  const socketUrl = readEnv("REACT_APP_SOCKET_URL") || DEFAULT_SOCKET_URL;

  const authConfigUrl =
    readEnv("REACT_APP_AUTH_CONFIG_URL") || deriveAuthConfigUrl(apiUrl);

  const authConfigFallbackUrl =
    readEnv("REACT_APP_AUTH_CONFIG_FALLBACK_URL") || apiUrl + "auth/config";

  /** nextgen (default) | legacy | react-player | jw — see resolvePlaybackEngine */
  const playbackEngine =
    readEnv("REACT_APP_PLAYBACK_ENGINE") || "nextgen";

  /** Classic Blade admin root (Sprint 10 CVM-12 Studio ↔ VMS deep links) */
  const adminBaseUrl = ensureTrailingSlash(
    readEnv("REACT_APP_ADMIN_URL") || deriveAdminBaseUrl(apiUrl)
  );

  return {
    apiUrl,
    settingsUrl,
    homeSettingsUrl,
    socketUrl,
    authConfigUrl,
    authConfigFallbackUrl,
    playbackEngine,
    adminBaseUrl,
  };
}

/** Resolved once at module load (CRA inlines env at build time). */
export const runtimeConfig = buildRuntimeConfig();

export default runtimeConfig;
